import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  OrderType,
  TriggerType,
  TimeInForce,
} from '@katanaperps/katana-perps-sdk';

import { ApiTriggerType } from 'api/types';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { trackEvent } from 'lib/analytics';
import { sanitizeAndTruncateNumber } from 'lib/numbers';
import { UnreachableCaseError } from 'lib/utils';

import { REDUCE_ONLY_AND_TPSL_COMBINE_ERROR } from './tpslUtils';

type OrderTriggerType = 'stopLoss' | 'takeProfit';

/**
 * Frontent representation of {@link OrderType.limit} and {@link OrderType.market}.
 * Is used to calculate SDK order type (eg. stopLossLimit) before order execution.
 */
type BaseOrderType = 'limit' | 'market';

/**
 * Frontent Dropdown representation of {@link OrderType}
 * Stored in reducer unwrapped as {@link OrderTriggerType} and {@link BaseOrderType}
 */
export type WrappedUserOrderType = BaseOrderType | 'stopLimit' | 'stopMarket';

const unwrapOrderType = (
  orderType: WrappedUserOrderType
): {
  baseOrderType: BaseOrderType;
  triggerType?: OrderTriggerType;
} => {
  const isTakeProfit = orderType.includes('takeProfit');
  const isStopLoss = orderType.includes('stop');
  return {
    baseOrderType: orderType.toLowerCase().includes('limit')
      ? 'limit'
      : 'market',
    triggerType: isTakeProfit
      ? 'takeProfit'
      : isStopLoss
      ? 'stopLoss'
      : undefined,
  };
};

export type BuySellConditionalTpSl = {
  side: 'long' | 'short';
  takeProfitPrice?: number;
  takeProfitPriceType?: 'last' | 'index';
  stopLossPrice?: number;
  stopLossPriceType?: 'last' | 'index';
};

export type PanelState = {
  /** State of panel. In confirm state we have to freeze all data */
  state:
    | 'input'
    | 'leverageModal'
    | 'loadingBuy'
    | 'loadingSell'
    | 'confirmBuy'
    | 'confirmSell'
    | 'successBuy'
    | 'successSell';
  errorMessage?: string;

  /** https://help.liquid.com/en/articles/3361237-how-do-i-set-stop-loss-and-take-profit-when-placing-an-order */
  orderType: BaseOrderType;

  /**
   * When a market’s status is limitMaker in the /markets endpoint, the Buy/Sell panel should go into post-only mode
   * Restrict Type to Limit, the user should not be able to choose market.
   * Hide the “Advanced toggle”. Always have the Post-Only setting true.
   */
  limitMakerOnlyMarket: boolean;

  /**
   * If Post-Only is selected, disable the “Time in Force” selection box
   * and only display “Good until canceled” (this is the only option available)
   */
  limitOrderTimeInForce: TimeInForce;

  price: string;

  sizeInQuote: boolean;
  /** For QUOTE TERMS orders (Buy Market Order), this represents TOTAL quantity amount */
  size: string;

  /** Has priority over "sizeInQuote" and "size" which should be empty in this case */
  sizePercentage: number;

  /** For all order types */
  reduceOnly: boolean;
  /** For all "limit" and "market stop" order types */
  postOnly: boolean;

  conditionalTpSlModal: boolean;
  /** TP/SL set on the Order. Activated after order is hit. */
  conditionalTpSl?: BuySellConditionalTpSl;

  triggerEnabled: boolean;
  triggerType: ApiTriggerType;
  triggerPrice?: string;
};

export type IAction =
  | {
      type: 'set';
      prop: 'price' | 'triggerPrice';
      value: string;
      priceDecimals: number;
      buyingPower: number;
    }
  | { type: 'changeTriggerType'; value: PanelState['triggerType'] }
  | {
      type: 'set';
      prop: 'size';
      value: string;
      quantityDecimals: number;
      prefillBestPrice?: string;
    }
  | {
      type: 'set';
      prop: 'sizePercentage';
      value: number;
      prefillBestPrice?: string;
    }
  | {
      type: 'set';
      prop: 'postOnly' | 'reduceOnly' | 'sizeInQuote';
      value: boolean;
    }
  | { type: 'setConditionalTpSl'; tpsl: PanelState['conditionalTpSl'] }
  | { type: 'showConditionlTpSlModal'; open: boolean }
  | { type: 'changeLimitOrderTimeInForce'; value: TimeInForce }
  | { type: 'changeType'; orderType: WrappedUserOrderType }
  | { type: 'error'; message: string }
  | { type: 'resetState' | 'closeConfirm' }
  | { type: 'loading' | 'success'; side: 'buy' | 'sell' }
  | { type: 'openConfirm'; isBuy: boolean }
  | { type: 'marketChanged'; limitMakerOnlyMarket: boolean }
  | { type: 'openLeverageModal' | 'closeLeverageModal' };

export const defaultState: PanelState = {
  state: 'input',
  errorMessage: undefined,
  orderType: 'limit',
  limitMakerOnlyMarket: false,
  limitOrderTimeInForce: TimeInForce.gtc,

  price: '',
  sizeInQuote: false,
  sizePercentage: 0,
  size: '',

  reduceOnly: false,
  postOnly: false,

  triggerEnabled: false,
  triggerType: TriggerType.last,
  triggerPrice: '',

  conditionalTpSlModal: false,
  conditionalTpSl: undefined,
};

export function reducer(state: PanelState, action: IAction): PanelState {
  const track = (action: string) =>
    trackEvent(`order_${action}`, { orderType: state.orderType });

  const actionType = action.type;
  console.debug('[buyPanelReducer] action:', action);
  switch (actionType) {
    case 'loading':
      track('starting');
      return {
        ...state,
        state: action.side === 'buy' ? 'loadingBuy' : 'loadingSell',
        errorMessage: undefined,
      };
    case 'openConfirm':
      track('confirmation_open');
      return {
        ...state,
        state: action.isBuy ? 'confirmBuy' : 'confirmSell',
        errorMessage: undefined,
      };
    case 'closeConfirm':
      track('confirmation_close');
      return {
        ...state,
        state: 'input',
      };
    case 'success':
      track('success');
      return {
        ...state,
        state: action.side === 'buy' ? 'successBuy' : 'successSell',
      };
    case 'error':
      track('error');
      return {
        ...state,
        state: 'input',
        errorMessage: action.message,
      };
    case 'openLeverageModal':
      return {
        ...state,
        state: 'leverageModal',
      };
    case 'closeLeverageModal':
      return {
        ...state,
        state: defaultState.state,
      };
    case 'changeLimitOrderTimeInForce':
      if (state.postOnly) {
        console.error('Cannot update "time force" with "Post-Only"');
        return state;
      }
      return {
        ...state,
        limitOrderTimeInForce: action.value,
      };
    case 'changeTriggerType':
      return {
        ...state,
        triggerType: action.value,
      };
    case 'changeType': {
      if (state.state === 'loadingBuy' || state.state === 'loadingSell') {
        return state;
      }
      const unwrappedOrderType = unwrapOrderType(action.orderType);
      return {
        ...defaultState,
        orderType: unwrappedOrderType.baseOrderType,
        triggerEnabled: !!unwrappedOrderType.triggerType,
        limitMakerOnlyMarket: state.limitMakerOnlyMarket,
        // Post isn't available with STOP
        postOnly: unwrappedOrderType.triggerType
          ? false
          : state.limitMakerOnlyMarket,
      };
    }
    case 'marketChanged':
      return action.limitMakerOnlyMarket
        ? {
            ...defaultState,
            orderType: 'limit',
            limitMakerOnlyMarket: true,
            postOnly: true,
          }
        : {
            ...defaultState,
            orderType: state.orderType,
          };
    case 'resetState':
      return {
        ...state,
        state: defaultState.state,
        errorMessage: defaultState.errorMessage,
      };
    case 'showConditionlTpSlModal':
      if (!action.open) {
        return {
          ...state,
          conditionalTpSlModal: false,
        };
      }
      if (state.reduceOnly) {
        console.error(REDUCE_ONLY_AND_TPSL_COMBINE_ERROR);
        return state;
      }
      return {
        ...state,
        conditionalTpSlModal: true,
      };
    case 'setConditionalTpSl':
      return {
        ...state,
        conditionalTpSlModal: false,
        conditionalTpSl: action.tpsl,
      };
    case 'set': {
      if (state.state === 'loadingBuy' || state.state === 'loadingSell') {
        return state;
      }

      if (state.state === 'confirmBuy' || state.state === 'confirmSell') {
        console.warn('BuySell panel in read-only confirm state');
        return state;
      }

      if (action.prop === 'sizeInQuote') {
        return {
          ...state,
          sizeInQuote: action.value,
          size: '',
          sizePercentage: 0,
        };
      }

      if (action.prop === 'postOnly') {
        return {
          ...state,
          postOnly: action.value,
          // If Post-Only is selected, disable the “Time in Force”
          // selection box and only display “Good until canceled”.
          // And this is the only option available.
          ...(action.value
            ? {
                limitOrderTimeInForce: TimeInForce.gtc,
                // TODO: BuySell: why was this disabled ?
                // triggerEnabled: false, // Stop = market order taker
              }
            : null),
        };
      }

      if (action.prop === 'reduceOnly') {
        if (state.conditionalTpSl) {
          console.error(REDUCE_ONLY_AND_TPSL_COMBINE_ERROR);
          return state;
        }
        return {
          ...state,
          reduceOnly: action.value,
        };
      }

      if (
        action.prop === 'sizePercentage'
        // We allow changing price when percentage is active
        // || (state.amountPercentage && action.prop === 'price')
      ) {
        return {
          ...state,
          size: '',
          sizePercentage: action.value,
          price: action.prefillBestPrice ?? state.price,
        };
      }
      if (
        action.prop !== 'size' &&
        action.prop !== 'price' &&
        action.prop !== 'triggerPrice'
      ) {
        console.error('Incorrect reducer action set-' + action.prop);
        return state;
      }

      /** Value in base can be later on truncated based on stepSize */
      const valueTruncated = sanitizeAndTruncateNumber(
        action.value,
        action.prop === 'price' || action.prop === 'triggerPrice'
          ? action.priceDecimals
          : action.prop === 'size'
          ? action.quantityDecimals
          : EXCHANGE_DECIMALS
      );

      const newState: PanelState =
        action.prop === 'size'
          ? // We want to reset slider once user enter size manually
            { ...state, size: valueTruncated, sizePercentage: 0 }
          : action.prop === 'price'
          ? { ...state, price: valueTruncated }
          : action.prop === 'triggerPrice'
          ? { ...state, triggerPrice: valueTruncated }
          : state;

      // There are not other fields which needs to be updated in market order
      if (state.orderType === 'market') {
        return {
          ...newState,
          errorMessage: undefined,
        };
      }

      // TODO: in v4 it's more complex to prefill price, as we don't know if user is going to buy or sell
      //       https://idexcrypto.slack.com/archives/C0380KS26NN/p1686762939467739
      // Prefill BEST PRICE when inputs are empty
      // See "Field Interaction" https://docs.google.com/document/d/1YWaxDt5zVT26zZX7LLeME16ammvPf_dQp-jnzr4yFFE/edit
      if (action.prop === 'size' && action.prefillBestPrice) {
        newState.price = sanitizeAndTruncateNumber(action.prefillBestPrice);
      }

      if (action.prop === 'price' || action.prop === 'size') {
        return {
          ...newState,
          errorMessage: undefined,
        };
      }
      return newState;
    }
    default: {
      throw new UnreachableCaseError(actionType);
    }
  }
}
